import { useState } from 'react';
import { Dialog } from '@m/alchemy-ui/Dialog';
import { Toast } from '@m/alchemy-ui/Toast';
import { defineMessages, useIntl } from 'react-intl';
import styled from 'styled-components';
import { type ToastMessageType } from '../../common/enums';
import { getConfirmToastMessage, getErrorToastMessage } from '../../helpers';

export interface MfaResetProps {
  readonly userId: string;
  readonly userName: string;
  readonly userEmail: string;
  readonly actionKey: string;
}

interface AdminUserWindow extends Window {
  Admin_User?: {
    view: () => void;
  };
}

const messages = defineMessages({
  title: {
    id: 'resetMfa',
    defaultMessage: 'Reset MFA'
  },
  confirm: {
    id: 'confirm',
    defaultMessage: 'Confirm'
  },
  cancel: {
    id: 'cancel',
    defaultMessage: 'Cancel'
  },
  body: {
    id: 'resetMfaConfirm',
    defaultMessage:
      'Are you sure you want to reset MFA for {userName} ({userEmail})? They will need to set up MFA again on their next login.'
  },
  success: {
    id: 'resetMfaSuccess',
    defaultMessage: 'MFA has been reset successfully.'
  },
  failed: {
    id: 'resetMfaFailed',
    defaultMessage: 'Failed to reset MFA.'
  }
});

const StyledDialog = styled(Dialog)`
  z-index: 1050;
`;

export const MfaReset = (props: MfaResetProps) => {
  const { userId, userName, userEmail, actionKey } = props;
  const intl = useIntl();

  const [isDialogOpen, setIsDialogOpen] = useState(true);
  const [toastMessages, setToastMessages] = useState<ToastMessageType[]>([]);

  const dialogTitle = intl.formatMessage(messages.title);
  const dialogConfirm = intl.formatMessage(messages.confirm);
  const dialogCancel = intl.formatMessage(messages.cancel);
  const dialogBody = intl.formatMessage(messages.body, { userName, userEmail });
  const resetSuccessful = intl.formatMessage(messages.success);
  const resetFailed = intl.formatMessage(messages.failed);

  const closeDialog = () => setIsDialogOpen(false);

  const handleConfirm = async () => {
    try {
      const formData = new FormData();
      formData.append('userId', userId);
      formData.append('ak', actionKey);

      const response = await fetch('/user/admin_resetMfa', {
        method: 'POST',
        headers: {
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: formData
      });

      if (!response.ok) {
        setToastMessages(getErrorToastMessage(resetFailed));
        return;
      }

      const data = await response.json();

      if (data?.success === true) {
        setToastMessages(getConfirmToastMessage(resetSuccessful));
        (window as AdminUserWindow).Admin_User?.view();
      } else {
        setToastMessages(getErrorToastMessage(resetFailed));
      }
    } catch {
      setToastMessages(getErrorToastMessage(resetFailed));
    }
  };

  return (
    <div data-testid="mfa-reset-container">
      <StyledDialog
        isOpen={isDialogOpen}
        onClose={closeDialog}
        size="small"
        headerContent={dialogTitle}
        bodyContent={dialogBody}
        footerButtons={[
          { label: dialogCancel, onClick: closeDialog, priority: 'tertiary' },
          {
            label: dialogConfirm,
            onClick: () => {
              handleConfirm();
              closeDialog();
            }
          }
        ]}
      />
      <Toast messages={toastMessages} />
    </div>
  );
};
